import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

// Role-based landing route. Finance is further sub-scoped:
//   - SUPPLIER  → /finance/purchase-orders  (e.g. FIN001)
//   - INVOICE   → /finance/invoices         (e.g. FIN002)
//   - KEDUANYA/null → /finance/dashboard
function getRedirectPath(user) {
  if (["admin", "super_admin", "master_admin", "supervisor"].includes(user.role)) return "/admin/dashboard";
  if (user.role === "finance") {
    const fr = (user.finance_role || "KEDUANYA").toUpperCase();
    if (fr === "SUPPLIER") return "/finance/purchase-orders";
    if (fr === "INVOICE") return "/finance/invoices";
    return "/finance/dashboard";
  }
  if (user.role === "marketing") return "/marketing/orders";
  if (user.role === "nakes") return "/nakes/jobs";
  return "/gudang/home";
}

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      const redirectPath = getRedirectPath(user);
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const user = await login(userId, password, {
        // Oct 2026 — beri tahu user kalau login sedang dicoba ulang karena sinyal.
        onRetry: () => toast.info("Sinyal lemah, mencoba lagi..."),
      });

      const redirectPath = getRedirectPath(user);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      // Oct 2026 — Bedakan masalah jaringan / server dari password salah.
      // Sebelumnya semua error tanpa respons tampil "Invalid User ID or Password".
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        return;
      }
      if (!error?.response) {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          toast.error("Tidak ada koneksi internet. Periksa sinyal atau WiFi Anda, lalu coba lagi.");
        } else {
          toast.error("Sinyal lemah atau server lambat merespons. Silakan coba lagi sebentar lagi.");
        }
        return;
      }
      if (error.response.status >= 500) {
        toast.error("Server sedang sibuk. Silakan coba lagi sebentar lagi.");
        return;
      }
      const detail = error.response?.data?.detail || "";
      // Map known backend messages to friendly Indonesian text
      const isInactive =
        /inactive/i.test(detail) ||
        /nonaktif/i.test(detail) ||
        /tidak aktif/i.test(detail);
      if (isInactive) {
        toast.error(
          "Akun Anda sedang tidak aktif. Apabila membutuhkan informasi lebih lanjut, silakan hubungi Tim Admin/HRD."
        );
      } else {
        toast.error(detail || "Invalid User ID or Password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 p-4" style={{ minHeight: '100dvh' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* FIX: Logo lebih besar, hapus text Vaxa Medika */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_vaxa-billing-system/artifacts/kvo7przr_LOGO.png"
              alt="Vaxa Medika"
              className="w-40 h-40 mx-auto object-contain mb-4"
            />
            <p className="text-gray-600 text-sm md:text-base">Healthcare Management System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                User ID
              </label>
              <input
                id="userId"
                type="text"
                placeholder="Enter your User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                disabled={loading}
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 text-sm md:text-base"
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center text-sm md:text-base"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </>
              ) : "Login"}
            </button>
          </form>

          <div className="mt-6 text-center text-xs md:text-sm text-gray-500">
            <p>Vaxa Medika Healthcare Management</p>
            <p className="mt-1">&copy; 2025 All rights reserved</p>
          </div>
        </div>
      </div>
    </div>
  );
}
